/**
 * Adaptive product-gallery height (custom - not stock Horizon).
 *
 * Problem: `slideshow-slides` is a flex row, so every slide stretches to the
 * tallest one. With a square first image and a 9:16 image later in the set, the
 * square slide is padded with empty space above and below.
 *
 * Each `.product-media-container` already carries its own
 * `--media-preview-ratio` in "adapt" mode, so the natural height per slide is
 * known - the track just never shrinks to it. This sets the track height to the
 * slide currently in view, so a square image fills its own frame and a tall
 * image is still shown in full.
 *
 * While a swipe is in progress the height is held at the MAX of the slides in
 * view, so an incoming taller image is never clipped mid-drag; it settles onto
 * the active slide's height once scrolling stops.
 */
(() => {
  const GALLERY = 'media-gallery.media-gallery--adaptive-height';
  const SETTLE_MS = 120;

  const mediaOf = (slide) => slide.querySelector('.product-media-container') || slide.firstElementChild;

  function naturalHeight(slide) {
    const media = mediaOf(slide);
    if (!media) return 0;
    // getBoundingClientRect reflects the aspect-ratio box, which is what we want
    return Math.round(media.getBoundingClientRect().height);
  }

  /** Slides whose horizontal span overlaps the visible window of the track. */
  function visibleSlides(track) {
    const slides = [...track.children].filter((el) => el.tagName === 'SLIDESHOW-SLIDE');
    if (!slides.length) return [];
    const left = track.scrollLeft;
    const right = left + track.clientWidth;
    return slides.filter((s) => s.offsetLeft < right - 1 && s.offsetLeft + s.offsetWidth > left + 1);
  }

  /** The slide closest to the centre of the track. */
  function activeSlide(track) {
    const slides = [...track.children].filter((el) => el.tagName === 'SLIDESHOW-SLIDE');
    if (!slides.length) return null;
    const centre = track.scrollLeft + track.clientWidth / 2;
    let best = slides[0];
    let bestDist = Infinity;
    for (const s of slides) {
      const d = Math.abs(s.offsetLeft + s.offsetWidth / 2 - centre);
      if (d < bestDist) {
        bestDist = d;
        best = s;
      }
    }
    return best;
  }

  function apply(track, { settled }) {
    const targets = settled ? [activeSlide(track)] : visibleSlides(track);
    const h = Math.max(0, ...targets.filter(Boolean).map(naturalHeight));
    if (!h) return;
    // avoid layout thrash on identical values
    if (track.dataset.appliedHeight === String(h)) return;
    track.dataset.appliedHeight = String(h);
    track.style.height = `${h}px`;
  }

  function setup(gallery) {
    const track = gallery.querySelector('slideshow-slides');
    if (!track || track.dataset.adaptiveHeight === 'on') return;
    track.dataset.adaptiveHeight = 'on';

    let frame = null;
    let settleTimer = null;

    const update = (settled) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => apply(track, { settled }));
    };

    track.addEventListener(
      'scroll',
      () => {
        update(false);
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => update(true), SETTLE_MS);
      },
      { passive: true }
    );

    // Images finishing load, viewport resizes, and variant swaps all change the
    // measured height; ResizeObserver catches every one of them.
    const ro = new ResizeObserver(() => update(true));
    ro.observe(track);
    [...track.children].forEach((slide) => {
      const media = mediaOf(slide);
      if (media) ro.observe(media);
    });

    update(true);
    // one more pass once late images have decoded
    window.addEventListener('load', () => update(true), { once: true });
  }

  function init() {
    document.querySelectorAll(GALLERY).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor section reloads and variant re-renders replace the gallery.
  document.addEventListener('shopify:section:load', init);
  let reinit = null;
  new MutationObserver(() => {
    clearTimeout(reinit);
    reinit = setTimeout(init, 50);
  }).observe(document.body, { childList: true, subtree: true });
})();
